/* eslint-disable @typescript-eslint/ban-ts-comment */
import { useEffect, useState } from 'react';
import Button from '../Shared/Button';
import Input from '../Shared/Input';
import TextArea from '../Shared/TextArea';
import { useFormik } from 'formik';
import * as Yup from 'yup';
import { useMutation, useQuery } from '@tanstack/react-query';
import { AxiosError } from 'axios';
import { ButtonTypes } from '../../utils/consts';
import Spinner from '../Shared/Spinner';
import { toast } from 'react-toastify';
import { queryClient } from '../../main';
import Upload from '../Shared/Upload';
import { GetAllInstructors } from '../../api/Instructor/GetAllInstructors';
import Selectbox from '../Shared/SelectBox';
import { AddBlogDTO, AddNewBlog } from '../../api/Blogs/AddBlog';
import { useParams } from 'react-router-dom';
import { GetBlogById } from '../../api/Blogs/GetBlogById';
import { Switch } from '@headlessui/react'
import { EditBlog } from '../../api/Blogs/EditBlog';
import { useNavigate } from 'react-router-dom';
interface option {
  id?: string;
  name: string;
}

const validationSchema = Yup.object({
  title: Yup.string().required('Blog name is required'),
  description: Yup.string().required('Blog description is required'),
  img_url: Yup.string().required('Display Image is required'),
  brief: Yup.string().required('Blog Brief is required'),
  instructorId: Yup.string().required('Instructor is required'),
});
interface ContentItem {
  id?:string
  title: string;
  text: string;
  img_url:string;
}

const AddBlog = ({ onSuccess }: { onSuccess?: () => void }) => {
  
  const [contents, setContents] = useState<ContentItem[]>([
    { title: '', text: '', img_url: '' }
  ]);
  const [enabled, setEnabled] = useState(false)
  const [selected, setSelected] = useState<option | null>(null);
  const navigate = useNavigate()
  const addContent = () => {
    setContents([...contents, { title: '', text: '', img_url: '' }]);
  };

  const removeContent = (index: number) => {
    const updatedContents = [...contents];
    updatedContents.splice(index, 1);
    setContents(updatedContents);
  };
  const { id } = useParams();
  const blogId = id || "";

  const { data: BlogsByIdDTO, isLoading: isBlogLoading } = useQuery({
      queryFn: () => GetBlogById(blogId),
      queryKey: ["blogs", blogId],
      staleTime: 0,
      refetchInterval: 0,
      enabled: !!id,
  });
  const blog = BlogsByIdDTO?.data;
  useEffect(() => {
    if (blog) {
      setEnabled(blog.isTrending || false);
    }
  }, [blog]);

  useEffect(() => {
    if (blog) {
      formik.setValues({
        title: blog.title,
        description: blog.description,
        img_url: blog.img_url,
        brief: blog.brief,
        instructorId: blog.instructorId,
        isTrending: blog.isTrending || enabled,
      });

      setContents(
        blog.contents?.map((content) => ({
          id:content.id,
          title: content.title,
          text: content.text,
          img_url: content.img_url || "",
        })) || []
        );

      setSelected({
        id: blog.instructor?.id,
        name: `${blog.instructor?.firstName} ${blog.instructor?.lastName}`,
      });
    }
  }, [blog]);

  const { mutate, error, isPending } = useMutation({
    mutationFn: AddNewBlog,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['blogs'] });
      toast.success('Blog Added successfully');
      formik.resetForm();
      onSuccess && onSuccess();
    },
    onError: () => {
      formik.setSubmitting(false);
    },
  });
  const { mutate: editMutate, isPending: isEditPending } = useMutation({
    mutationFn: (data: AddBlogDTO) => EditBlog(data, blogId),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["blogs"] });
      toast.success("Blog Edited successfully");
      formik.setSubmitting(true);
      navigate('/dashboard/blogs')
    },
    onError: () => {
      formik.setSubmitting(false);
    },
  });

  const { data: InstructorDTO } = useQuery({
    queryFn: GetAllInstructors,
    queryKey: ['instructors'],
    staleTime: 0,
    refetchInterval: 0,
  });

  const Instructor = InstructorDTO?.data?.map(instructor => ({
    id: instructor.id,
    name: `${instructor.firstName} ${instructor.lastName}`,
  })) || [];

  const handleSelectInstructor = (option: option | null) => {
    setSelected(option);
    if (option) {
      formik.setFieldValue('instructorId', option.id);
    } else {
      formik.setFieldValue('instructorId', '');
    }
  };

  const formik = useFormik({
    initialValues: {
      title: '',
      description: '',
      img_url: '',
      brief: '',
      instructorId: '',
      isTrending: false,
    },
    enableReinitialize: true,
    validationSchema,
    onSubmit: (values) => {
      const payload = {
        title: values.title,
        description: values.description,
        img_url: values.img_url,
        instructorId: values.instructorId,
        brief: values.brief,
        isTrending: enabled,
        contents: contents.map(content => ({
          id: content.id,
          title: content.title,
          text: content.text,
          img_url: content.img_url,
        })),
      };
      if (blogId) {
        editMutate(payload);
      } else {
        mutate(payload);
      }
    },
  });
  const handleTrendingToggle = (value: boolean) => {
    setEnabled(value);
    formik.setFieldValue("isTrending", value);
  };

  const handleimgUrlUpload = (url: string) => {
    formik.setFieldValue('img_url', url);
  };
  if (isBlogLoading)
    return (
      <div className="min-h-screen flex items-center justify-center">
        <Spinner />
      </div>
    );


  return (
    <form onSubmit={formik.handleSubmit} className="pt-5">
      <div className="flex justify-between mb-6">
        <h1 className="text-burntBlack font-bold text-[32px] mt-5">          
          {id ? 'Edit Blog' : 'Add new blog'}
        </h1>
      </div>
      <div className="p-8 rounded-3xl shadow bg-white border border-black/15 min-h-[70vh]">
        <div className='flex gap-10'>
          <div className="max-w-[508px]">
            <h1 className="mb-2.5 font-bold text-2xl">Blog info</h1>
            <p className="text-lightGray font-medium">
              Add blog details to help traders discover your trading world and better understand what they will learn, what they
              will need, and expect.
            </p>
            <div className="space-y-7 pt-8">
              <Input
                value={formik.values.title}
                onChange={formik.handleChange}
                onBlur={formik.handleBlur}
                error={formik.touched.title && formik.errors.title ? formik.errors.title : ''}
                className="text-lg font-semibold"
                inputStyles="bg-[#F5F6F8] !h-12 border-black/15 !rounded"
                title="Blog Title"
                placeholder=""
                name="title"
                type="text"
              />
              <Upload folder="blogs_images" onFileUploaded={handleimgUrlUpload}>
                <Input
                  upload={true}
                  className="text-lg font-semibold"
                  inputStyles="bg-[#F5F6F8] !h-12 border-black/15 !rounded"
                  placeholder=""
                  title="Blog Image"
                  name="img_url"
                  type="text"
                  value={formik.values.img_url}
                  onChange={formik.handleChange}
                  onBlur={formik.handleBlur}
                  error={
                    formik.touched.img_url && formik.errors.img_url ? formik.errors.img_url : ''
                  }
                />
              </Upload>
              <Selectbox title="Instructor" className="bg-[#F5F6F8] !h-12 border-black/15 !rounded" name="instructorId" setSelected={handleSelectInstructor} selected={selected} selectOptions={Instructor} />
              <TextArea
                value={formik.values.description}
                onChange={formik.handleChange}
                onBlur={formik.handleBlur}
                error={
                  formik.touched.description && formik.errors.description
                    ? formik.errors.description
                    : ''
                }
                name="description"
                title="Blog Description"
                placeholder=""
                inputStyles="!bg-[#F5F6F8] !h-[170px] border-black/15"
              />
              <TextArea
                value={formik.values.brief}
                onChange={formik.handleChange}
                onBlur={formik.handleBlur}
                error={
                  formik.touched.brief && formik.errors.brief
                    ? formik.errors.brief
                    : ''
                }
                name="brief"
                title="Blog Brief"
                placeholder=""
                inputStyles="!bg-[#F5F6F8] !h-[120px] border-black/15"
              />
                          <div className={`md:space-y-2 space-y-1.5 relative font-medium`}>
              <h1 className="text-burntBlack md:text-base text-sm">
                Is Trending
              </h1>
              <Switch
                checked={enabled}
                onChange={handleTrendingToggle}
                className={`${enabled ? 'bg-secondary' : 'bg-primary'}
          relative inline-flex h-[38px] w-[74px] shrink-0 cursor-pointer rounded-full border-2 border-transparent transition-colors duration-200 ease-in-out focus:outline-none focus-visible:ring-2  focus-visible:ring-white/75`}
              >
                <span
                  aria-hidden="true"
                  className={`${enabled ? 'translate-x-9' : 'translate-x-0'}
            pointer-events-none inline-block h-[34px] w-[34px] transform rounded-full bg-white shadow-lg ring-0 transition duration-200 ease-in-out`}
                />
              </Switch>
            </div>
            </div>
          </div>

          <div className="w-full">
            {contents.map((content, index) => (
              <div key={index} className="space-y-7 pt-8 blog-content">
                <Input
                  value={content.title}
                  onChange={(e) => {
                    const updatedContents = [...contents];
                    updatedContents[index].title = e.target.value;
                    setContents(updatedContents);
                  }}
                  className="text-lg font-semibold"
                  inputStyles="bg-[#F5F6F8] !h-12 border-black/15 !rounded"
                  title={`Blog Title ${index + 1}`}
                  placeholder=""
                  name={`contentTitle-${index}`}
                  type="text"
                />
                <Upload
                  folder={`blogs_content_${index}_images`}
                  onFileUploaded={(url) => {
                    const updatedContents = [...contents];
                    updatedContents[index].img_url = url;
                    setContents(updatedContents);
                  }}
                >
                  <Input
                    upload={true}
                    className="text-lg font-semibold"
                    inputStyles="bg-[#F5F6F8] !h-12 border-black/15 !rounded"
                    placeholder=""
                    title={`Blog Img ${index + 1}`}
                    name={`contentImageUrl-${index}`}
                    type="text"
                    value={content.img_url}
                    onChange={(e) => {
                      const updatedContents = [...contents];
                      updatedContents[index].img_url = e.target.value;
                      setContents(updatedContents);
                    }}
                  />
                </Upload>
                <TextArea
                  value={content.text}
                  onChange={(e) => {
                    const updatedContents = [...contents];
                    updatedContents[index].text = e.target.value;
                    setContents(updatedContents);
                  }}
                  name={`contentText-${index}`}
                  title={`Blog Description ${index + 1}`}
                  placeholder=""
                  inputStyles="!bg-[#F5F6F8] !h-[150px] border-black/15"
                />
                {index === 0 && ( // Ensure at least one content section exists
                  <div className="flex w-full justify-between mt-5">
                    <Button
                      type={ButtonTypes.BUTTON}
                      className="before:!bg-primary hover:text-white !text-base !font-bold !h-9 w-20 !border-primary !border-2 text-primary"
                      variant="transparent"
                      onClick={() => removeContent(index)}
                    >
                      Cancel
                    </Button>
                    <Button
                      type={ButtonTypes.BUTTON}
                      variant="primary"
                      className="!h-9 w-20 !text-base !font-bold"
                      onClick={addContent}
                    >
                      Add
                    </Button>
                  </div>
                )}
              </div>
            ))}
          </div>
        </div>
        {error instanceof AxiosError && (
          <span className="text-red-500 font-bold text-lg">
            {error?.response?.data?.message || 'An error occurred'}
          </span>
        )}

<Button className="!h-14 w-full !text-lg font-bold rounded-md !bg-[#4379EE] hover:!bg-primary" variant="primary" type={ButtonTypes.SUBMIT}
              disabled={formik.isSubmitting || isPending || isEditPending}
            >Done           {isPending ? (
              <Spinner />) : ('')}
            </Button>
      </div>
    </form>
  );
};

export default AddBlog;
